/**
 * Shared teardown seam for ability state that is keyed to a local network
 * session. A fresh Colyseus room can replace the local session id while the
 * browser and player object stay alive, so per-session listeners and timers
 * must be released on both terminal disconnect and session rekey.
 */

import { EventBus, EVENTS } from '../../../../shared/EventBus';

export type AbilitySessionReleaseHook = (playerId?: string) => void;
/** Re-arm a session-keyed owner after a reconnect handed us a new session id. */
export type AbilitySessionRearmHook = (sessionId: string) => void;

const releaseHooks = new Set<AbilitySessionReleaseHook>();
const rearmHooks = new Set<AbilitySessionRearmHook>();
let boundBus: EventBus | null = null;
let lifecycleUnsubscribers: Array<() => void> = [];
let currentNetworkSessionId: string | null = null;

function ensureLifecycleSubscriptions(): void {
  const bus = EventBus.getInstance();
  if (boundBus === bus) return;

  for (const unsubscribe of lifecycleUnsubscribers) unsubscribe();
  lifecycleUnsubscribers = [];
  boundBus = bus;

  lifecycleUnsubscribers.push(
    bus.on(EVENTS.NET_CONNECTED, ({ sessionId }) => {
      if (
        currentNetworkSessionId
        && sessionId
        && currentNetworkSessionId !== sessionId
      ) {
        releaseAbilitySession(currentNetworkSessionId);
      }
      currentNetworkSessionId = sessionId || currentNetworkSessionId;
      // The seam was release-only: nothing ever asked an owner to arm again.
      // A passive is registered when its ability function runs and no pass
      // re-runs it after a reconnect, so the character silently lost every
      // passive until the player re-cast it by hand.
      if (sessionId) rearmAbilitySession(sessionId);
    }),
    bus.on(EVENTS.NET_DISCONNECTED, ({ sessionId }) => {
      const departedSessionId = sessionId || currentNetworkSessionId || undefined;
      releaseAbilitySession(departedSessionId);
      if (!sessionId || sessionId === currentNetworkSessionId) {
        currentNetworkSessionId = null;
      }
    }),
  );
}

/** Register one owner of session-keyed ability resources. */
export function registerAbilitySessionReleaseHook(
  release: AbilitySessionReleaseHook,
): () => void {
  ensureLifecycleSubscriptions();
  releaseHooks.add(release);
  return () => releaseHooks.delete(release);
}

/**
 * Release one departed session. An omitted id is the legacy disconnect
 * fallback and asks every owner to clear all of its active session state.
 */
export function releaseAbilitySession(playerId?: string): void {
  for (const release of [...releaseHooks]) release(playerId);
}

/** Register one owner that can re-arm itself for a new session id. */
export function registerAbilitySessionRearmHook(
  rearm: AbilitySessionRearmHook,
): () => void {
  ensureLifecycleSubscriptions();
  rearmHooks.add(rearm);
  return () => rearmHooks.delete(rearm);
}

/**
 * Ask every owner to arm for `sessionId`.
 *
 * Owners decide what they can honestly restore. A session-scoped release
 * (`releaseAbilitySession(id)`) means a reconnect is expected, so an owner may
 * hold its per-player registration dormant and revive it here. An UNSCOPED
 * release is the teardown/logout path and must discard everything — otherwise
 * logging out as one character and back in as another would revive the first
 * character's passives against a dead player object.
 */
export function rearmAbilitySession(sessionId: string): void {
  for (const rearm of [...rearmHooks]) rearm(sessionId);
}
